import React, { useEffect, useState } from "react";
import { phoneRegExp } from "@utils/validation";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMst } from "@store/Route";
import Swal from "sweetalert2";
import { PostalCodeType } from "@store/PostalCode";
import { ProfileUserType } from "@store/Profile";
import { AlertError } from "@utils/alert";

interface ProfileHookProps {
  token: string | null;
  getProfile: (token: string) => Promise<void>;
  profileUser: ProfileUserType | null;
  updateProfile: (
    token: string,
    profileUser: any,
    callback: () => void
  ) => Promise<void>;
}

const ProfileHook = ({
  token,
  getProfile,
  profileUser,
  updateProfile,
}: ProfileHookProps) => {
  useEffect(() => {
    if (token) getProfile(token);
  }, []);

  const BillingEmails: string[] =
    profileUser?.billingEmails?.map((billingEmail) => {
      return billingEmail.email;
    }) || [];

  const formik = useFormik({
    initialValues: {
      salutation: profileUser?.salutation || "",
      firstName: profileUser?.firstName || "",
      lastName: profileUser?.lastName || "",
      email: profileUser?.user?.email || "",
      mobileNo: profileUser?.mobile || "",
      password: "",
      retypePassword: "",
      // vaNumber: profileUser?.vaNumber || "",
      billingName: profileUser?.billingAddress.name || "",
      blockNo: profileUser?.billingAddress.blockNumber || "",
      address: profileUser?.billingAddress.address || "",
      buildingName: profileUser?.billingAddress.building || "",
      postalCode: profileUser?.billingAddress.postalCode || "",
      unitNo: profileUser?.billingAddress.unit || "",
      billingEmails: BillingEmails,
      token: profileUser?.securityCode || "",
    },
    validationSchema: Yup.object({
      salutation: Yup.string().required("Salutation is required"),
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      mobileNo: Yup.string()
        .matches(phoneRegExp, "Phone number is not valid")
        .required("Phone number is required"),
      password: Yup.string().min(6, "Password must be at least 6 characters"),
      billingName: Yup.string().required("Billing Name is required"),
      blockNo: Yup.string().required("Block No is required"),
      address: Yup.string().required("Address is required"),
      buildingName: Yup.string().required("Building Name is required"),
      postalCode: Yup.string().required("Postal Code is required"),
      billingEmails: Yup.array()
        .ensure()
        .of(Yup.string().email("Invalid email address")),
      unitNo: Yup.string().nullable().notRequired(),
    }),
    onSubmit: (values) => {
      if (token) {
        const billingEmails = values.billingEmails.map(
          (billingEmail, number) => {
            return {
              id: number,
              email: billingEmail,
            };
          }
        );
        updateProfile(
          token,
          {
            id: profileUser?.id,
            user: {
              email: values.email,
              password: values.password || null,
            },
            salutation: values.salutation,
            firstName: values.firstName,
            lastName: values.lastName,
            securityCode: values.token,
            mobile: values.mobileNo,
            // vaNumber: values.vaNumber,
            status: "active",
            billingAddress: {
              blockNumber: values.blockNo,
              postalCode: values.postalCode,
              building: values.buildingName,
              address: values.address,
              unit: values.unitNo,
              name: values.billingName,
            },
            billingEmails: billingEmails,
          },
          () => {
            Swal.fire({
              icon: "success",
              title: "Update Profile has been success",
              timer: 1500,
            }).then(() => {
              window.location.reload();
            });
          }
        );
      }
    },
  });

  useEffect(() => {
    if (profileUser) {
      formik.setValues({
        salutation: profileUser?.salutation || "",
        firstName: profileUser?.firstName || "",
        lastName: profileUser?.lastName || "",
        email: profileUser?.user?.email || "",
        mobileNo: profileUser?.mobile || "",
        password: "",
        retypePassword: "",
        // vaNumber: profileUser?.vaNumber || "",
        billingName: profileUser?.billingAddress.name || "",
        blockNo: profileUser?.billingAddress.blockNumber || "",
        address: profileUser?.billingAddress.address || "",
        buildingName: profileUser?.billingAddress.building || "",
        postalCode: profileUser?.billingAddress.postalCode || "",
        unitNo: profileUser?.billingAddress.unit || "",
        billingEmails: BillingEmails,
        token: profileUser?.securityCode || "",
      });
    }
  }, [profileUser]);

  const cancelEdit = () => {
    formik.setValues({
      salutation: profileUser?.salutation || "",
      firstName: profileUser?.firstName || "",
      lastName: profileUser?.lastName || "",
      email: profileUser?.user?.email || "",
      mobileNo: profileUser?.mobile || "",
      password: "",
      retypePassword: "",
      // vaNumber: profileUser?.vaNumber || "",
      billingName: profileUser?.billingAddress.name || "",
      blockNo: profileUser?.billingAddress.blockNumber || "",
      address: profileUser?.billingAddress.address || "",
      buildingName: profileUser?.billingAddress.building || "",
      postalCode: profileUser?.billingAddress.postalCode || "",
      unitNo: profileUser?.billingAddress.unit || "",
      billingEmails: BillingEmails,
      token: profileUser?.securityCode || "",
    });
  };

  return {
    formik,
    cancelEdit,
  };
};

export default ProfileHook;
